import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { wormColors } from "core/src/core/constants.js";
import { C, LB } from "./theme";
import Icon from "./Icon";
import { useNarrow } from "./useNarrow";

// A worm-color picker for the setup screens. The popover renders in a portal
// (fixed position), so it can't be clipped by the scrolling columns: on desktop
// it opens below the swatch, or flips above when there isn't room below; on a
// phone it becomes a full-screen sheet with big tap targets.
const COLS = 6;
const FILL = 30; // the coloured square size
const CELLPAD = 3; // half the visual gap; the whole cell is clickable (no dead space between colours)
const PAD = 8;
const PANEL_W = PAD * 2 + COLS * (FILL + CELLPAD * 2); // 220

// A read-only swatch that matches the picker's trigger exactly (same .sc-square
// styling, size and shape). Used by rosters where the colour isn't editable from
// here (a joined remote player, a server bot), so those rows share one swatch look
// with the editable local rows instead of hand-rolling their own box.
export function ColorSwatch({ colorId, size = 30 }: { colorId: string; size?: number }) {
  return <span className="sc-square" style={{ display: "block", width: size, height: size, flex: "0 0 auto", background: (wormColors as any)[colorId] || "#888" }} />;
}

export default function ColorSwatchPicker({ colorId, colors, onChange }: { colorId: string; colors: string[]; onChange: (id: string) => void }) {
  const narrow = useNarrow();
  const trigRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  // Close on Escape, and on scroll/resize (the fixed popover would otherwise drift
  // away from its now-moved trigger).
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("resize", close);
    window.addEventListener("scroll", close, true);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("resize", close);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const openPicker = () => {
    if (!narrow) {
      const r = trigRef.current!.getBoundingClientRect();
      const rows = Math.ceil(colors.length / COLS);
      const panelH = PAD * 2 + rows * (FILL + CELLPAD * 2);
      const spaceBelow = window.innerHeight - r.bottom;
      const flipUp = spaceBelow < panelH + 12 && r.top > spaceBelow; // not enough below, more above
      // Round to whole pixels - a fixed element on a fractional boundary renders blurry.
      const top = Math.round(flipUp ? Math.max(8, r.top - panelH - 6) : r.bottom + 6);
      const left = Math.round(Math.max(8, Math.min(r.left, window.innerWidth - PANEL_W - 8)));
      setPos({ top, left });
    }
    setOpen(true);
  };

  const pick = (id: string) => { onChange(id); setOpen(false); };

  // The button fills its whole grid cell (so there's no dead space to click
  // between colours); the visible square is an inner span inset by the padding.
  const swatch = (id: string) => (
    <button
      key={id}
      type="button"
      className={"sc-cell" + (id === colorId ? " sc-cell--active" : "")}
      onClick={() => pick(id)}
      title={id}
      style={{ width: "100%", aspectRatio: "1 / 1" }}
    >
      <span className="sc-square" style={{ display: "block", width: "100%", height: "100%", background: (wormColors as any)[id] }} />
    </button>
  );

  return (
    <>
      <button ref={trigRef} type="button" className="sc-square sx-plain" onClick={openPicker} title="Change color" style={{ width: 30, height: 30, flex: "0 0 auto", padding: 0, cursor: "pointer", background: (wormColors as any)[colorId] }} />
      {open && createPortal(
        <>
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 4000, background: narrow ? "rgba(3,6,12,0.72)" : "transparent" }} />
          {narrow ? (
            <div style={{ position: "fixed", zIndex: 4001, left: "50%", top: "50%", transform: "translate(-50%, -50%)", width: "min(420px, 94vw)", maxHeight: "82vh", display: "flex", flexDirection: "column", background: C.panel, border: `1px solid ${C.line}`, borderRadius: 14, boxShadow: "0 20px 60px rgba(0,0,0,0.6)", color: C.txt }}>
              <div style={{ display: "flex", alignItems: "center", padding: "14px 16px", borderBottom: `1px solid ${C.line}`, flex: "0 0 auto" }}>
                <span style={{ fontFamily: LB, fontWeight: 900, fontSize: 16, flex: 1 }}>Choose your color</span>
                <button type="button" onClick={() => setOpen(false)} className="sx-plain" style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", display: "inline-flex", padding: 0 }}><Icon name="close" size={20} color={C.muted} /></button>
              </div>
              <div style={{ padding: 12, overflowY: "auto", display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(48px, 1fr))" }}>
                {colors.map((id) => swatch(id))}
              </div>
            </div>
          ) : (
            <div style={{ position: "fixed", zIndex: 4001, top: pos.top, left: pos.left, width: PANEL_W, boxSizing: "border-box", display: "grid", gridTemplateColumns: `repeat(${COLS}, 1fr)`, padding: PAD, background: C.panel2, border: `1px solid ${C.line}`, borderRadius: 10, boxShadow: "0 14px 34px rgba(0,0,0,0.55)" }}>
              {colors.map((id) => swatch(id))}
            </div>
          )}
        </>,
        document.body
      )}
    </>
  );
}
